import * as React from 'react';
import SignatureCanvas from 'react-signature-canvas';

import { ApplicationState, ConnectedReduxProps } from '../../../../store';
import { Modal } from '@mui/material';
import { CounterExpertiseDTO as CounterExpertise, SigningDTO as SigningType, SvsDTOLine as SvsLine, ValueAndCauseDTO as ValueAndCause, VatAndPaymentDTO as VatAndPayment } from '../../../../business/models/dto/svs/svs.dto';
import { setValidated, tabSigningFieldChange, updateSigning, validateSvs } from '../../../../store/details/svs/actions';

import BelfiusCurrencyText from '../../../custom/belfius-currency-text';
import ClearIcon from '@mui/icons-material/Clear';
import { Dispatch } from 'redux';
import DoneIcon from '@mui/icons-material/Done';
import { Ii18n } from '../../../../store/utils/contracts/i18n.interface';
import { InsuranceLogo } from '../../../custom/insurance-logo';
import { PolicySubTypeTCEnum } from '../../../../store/details/policy/types';
import { TypeCodeTranslation } from '../../../../business/models/shared.model';
import { connect } from 'react-redux';
import { getI18nReport } from '../../../../store/utils/i18n/strings.manager';
import { getTypeCodeTranslation } from '../../../../store/utils/typecodes/typecode-translations';
import styles from './signing.module.css';
import { Company, Role } from '../../../../store/shared/model';
import Fab from '@mui/material/Fab';
import { Checkbox } from '@mui/material';

interface PropsFromState {
  readonly insuredAmountBuilding: number | '';
  readonly insuredAmountContent: number | '';
  readonly valueAndCause: ValueAndCause;
  readonly svsLines: SvsLine[];
  readonly vatAndPayment: VatAndPayment;
  readonly insuredPersonName: string;
  readonly insuredAddress: string;
  readonly createdByName: string;
  readonly createdByCompany: string;
  readonly createdByCompanyDibCode: string;
  readonly counterExpertise: CounterExpertise;
  readonly signing: SigningType;
  readonly caseNumber: string;
  readonly policyCode: string;
  readonly docNotificationDamageLossDate: Date | '';
  readonly remark: string;
  readonly locked: boolean;
  readonly validated: boolean;
  readonly insuredPersonLanguageTC: string;
  readonly policySubTypeTC: string;
  readonly contractName: string;
  readonly SVSSubjectToTC: TypeCodeTranslation[];
  readonly SVSApplicationOfTheExemptionTC: TypeCodeTranslation[];
  readonly excessApplicableTC: string | undefined;
  readonly underConditionTC: string | undefined;
  readonly svsBlocked: boolean;
  readonly caseCode: string;
  readonly assignedUserName: string;
  readonly assignedUserParentPartyName: string;
  readonly assignedUserParentDibCode: string;
}

interface State {
  readonly modalOpened: boolean;
  readonly selectedModalSignature: SignatureTypes;
  readonly signaturePadWidth: number;
  readonly signaturePadHeight: number;
}

interface PropsFromDispatch {
  readonly updateSigning: typeof updateSigning;
  readonly updateValues: typeof tabSigningFieldChange;
  readonly validateSvs: typeof validateSvs;
  readonly setValidated: typeof setValidated;
}

type AllProps = PropsFromState & ConnectedReduxProps & PropsFromDispatch;

const enum SignatureTypesEnum {
  COUNTER_EXPERT = 'COUNTER_EXPERT',
  PERSONAL_EXPERT = 'PERSONAL_EXPERT',
  POLICY_HOLDER = 'POLICY_HOLDER',
  EXPERT_OR_INSPECTOR = 'EXPERT_OR_INSPECTOR'
}

type SignatureTypes = '' | SignatureTypesEnum;

class Signing extends React.Component<AllProps, State> {
  private localization: Ii18n;
  private signatureCanvas: any;
  private modalIsOpen: boolean = false;

  constructor(props: AllProps) {
    super(props);

    this.state = {
      modalOpened: false,
      selectedModalSignature: '',
      signaturePadWidth: 800,
      signaturePadHeight: 400
    };

    this.signatureCanvas = React.createRef<any>();

    this.localization = getI18nReport();
    this.localization.setLanguage(this.props.insuredPersonLanguageTC);
  }  

  componentDidMount() {
    if (this.state.modalOpened) {
      this.fixSizePositioning();
    }
    window.addEventListener('resize', this.fixSizePositioning);
  }
  componentDidUpdate(prevProps: AllProps, prevState: State) {
    if (this.state.modalOpened && !prevState.modalOpened) {
      this.fixSizePositioning();
    }
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.fixSizePositioning);
  }


  private signatureTemplate(signature: string, selectedSignature: SignatureTypes) {
    if (signature) {
      return <img alt="" src={signature} className={styles.img} />;
    }

    if ((selectedSignature === SignatureTypesEnum.PERSONAL_EXPERT && !this.props.signing.personalExpertSignedName) ||
      (selectedSignature === SignatureTypesEnum.COUNTER_EXPERT && !this.props.counterExpertise.counterExpertSignedName)) {
      return (
        <div className={styles.emptySignature}></div>
      );
    } else {
      return (
        <div className={`${styles.emptySignature} ${styles.clickable}`} onClick={() => {
          this.validateAndSign(selectedSignature);
        }}></div>
      );
    }
  }

  private async validateAndSign(selectedSignature: SignatureTypes) {
    if (this.props.svsBlocked) { return; }
    if (!this.props.locked) {
      await this.props.validateSvs();
    }

    if (this.props.locked || this.props.validated) {
      this.setState({ selectedModalSignature: selectedSignature });
      this.openSignatureModal();
    }
  }

  private get personalExpertSignedName() {
    if (this.props.signing.signaturePersonalExpert && this.props.signing.signaturePersonalExpert.length > 0) {
      return (
        <span>{this.props.signing.personalExpertSignedName}</span>
      );
    } else {
      return (
        <input className={styles.input}
          placeholder={this.localization.get('details.svs.signing.name')}
          value={this.props.signing.personalExpertSignedName}
          autoComplete='off'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (this.props.svsBlocked) { return; }
            this.props.updateValues({ ...this.props.signing, personalExpertSignedName: event.target.value });
          }} />
      );
    }
  }

  private get endText() {
    switch (this.props.policySubTypeTC) {
      case PolicySubTypeTCEnum.BELFIUS_HOME:
        return <span>{this.localization.get('details.svs.signing.belfius')}</span>;
      case PolicySubTypeTCEnum.DVV_BRAND:
        return <span>{this.localization.get('details.svs.signing.dvv')}</span>;
      default:
        return '';
    }
  }

  addSignature(): void {
    if (this.props.svsBlocked) { return; }

    const signaturePad = this.signatureCanvas.current;

    if (signaturePad) {
      if (signaturePad.toData().length === 0) {
        return;
      }
    }

    if (signaturePad.isEmpty()) {
        return;
    }

    switch (this.state.selectedModalSignature) {
      case SignatureTypesEnum.COUNTER_EXPERT:
        this.props.updateSigning({ ...this.props.signing, signatureCounterExpert: signaturePad.toDataURL() }, this.props.caseCode);
        break;
      case SignatureTypesEnum.EXPERT_OR_INSPECTOR:
        this.props.updateSigning({ ...this.props.signing, signatureExpertOrInspector: signaturePad.toDataURL() }, this.props.caseCode);
        break;
      case SignatureTypesEnum.PERSONAL_EXPERT:
        this.props.updateSigning({ ...this.props.signing, signaturePersonalExpert: signaturePad.toDataURL() }, this.props.caseCode);
        break;
      case SignatureTypesEnum.POLICY_HOLDER:
        this.props.updateSigning({ ...this.props.signing, signaturePolicyHolder: signaturePad.toDataURL() }, this.props.caseCode);
        break;
      default:
        break;
    }
    this.closeSignatureModal();
  }

  private fixSizePositioning() {
    if (!this.modalIsOpen) return;

    const canvas = this.signatureCanvas.current?._canvas;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;

    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;

    const context = canvas.getContext('2d');
    if (context) {
        context.scale(ratio, ratio);
    }
  }

  private openSignatureModal() {
    if (this.props.svsBlocked) { return; }
    this.modalIsOpen = true;
    this.setState({
      modalOpened: true
    });
  }

  private closeSignatureModal() {
    this.modalIsOpen = false;
    this.setState({
      modalOpened: false
    });
    this.props.setValidated(false);
  }

  private handleClear(): void {
    const signaturePad = this.signatureCanvas.current;

    if (signaturePad) {
        signaturePad.instance.clear();
    }
  }

  private get insuredData() {
    if (this.props.valueAndCause.cloned) {
      if (this.props.locked) {
        return (
          <span>
            {this.props.signing.cloneSuffererName}
            {this.localization.get('details.svs.signing.infoLivingAt')}&nbsp;
          {this.props.signing.cloneSuffererLocation}
          </span>
        );
      }
      return (
        <span>
          <input className={styles.input}
            placeholder={this.localization.get('details.svs.signing.name')}
            value={this.props.signing.cloneSuffererName}
            autoComplete='off'
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (this.props.svsBlocked) { return; }
              this.props.updateValues({ ...this.props.signing, cloneSuffererName: event.target.value });
            }} />
          {this.localization.get('details.svs.signing.infoLivingAt')}&nbsp;
          <input className={styles.input}
            placeholder={this.localization.get('details.svs.signing.address')}
            value={this.props.signing.cloneSuffererLocation}
            autoComplete='off'
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (this.props.svsBlocked) { return; }
              this.props.updateValues({ ...this.props.signing, cloneSuffererLocation: event.target.value });
            }} />
        </span>
      );
    }
    return (
      <span>
        {this.props.insuredPersonName}
        {this.localization.get('details.svs.signing.infoLivingAt')}&nbsp;
          {this.props.insuredAddress}
      </span>
    );
  }

  render() {
    const svsLinesOverview = this.props.svsLines.map(r => {
      return (
        <div className={styles.row} key={r.id}>
          <div className={`${styles.item6x} ${styles.text}`}>
            <span>{r.description}</span>
          </div>
          <div className={`${styles.item} ${styles.text} ${styles.alignRight}`}>
            {r.amount > 0 ?
              <BelfiusCurrencyText className={styles.alignRight}
                value={r.amount}
                prefix={'€ '} />
              : ''
            }
          </div>
        </div>
      );
    });

    return (
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.column}>
            <div className={styles.logo}>
              <InsuranceLogo policySubTypeTC={this.props.policySubTypeTC} languageTC={this.props.insuredPersonLanguageTC} />
            </div>
          </div>
          <div className={styles.column}>
            <div className={styles.info}>
              <div>{this.localization.get('details.svs.signing.belfiusAddress')}</div>
              <div>{this.localization.get('details.svs.signing.belfiusCompanyInfo')}</div>
              <div>{this.localization.get('details.svs.signing.belfiusBankInfo')}</div>
            </div>
          </div>
          <div className={styles.column6x}>
            <div className={`${styles.title} ${styles.border}`}>
              <span><b>{this.localization.get('details.svs.signing.title')}</b></span>
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.column}>
            <div className={styles.row}>
              <div className={styles.item}>
                <span><b>{this.localization.get('details.svs.signing.caseCode')}</b></span>
              </div>
              <div className={styles.item}>
                <span>{this.props.caseNumber}</span>
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.item}>
                <span><b>{this.localization.get('details.svs.signing.policyCode')}</b></span>
              </div>
              <div className={styles.item}>
                <span>{this.props.policyCode}</span>
              </div>
            </div>
          </div>
          <div className={styles.column}>
            <div className={styles.row}>
              <div className={styles.item}>
                <span><b>{this.localization.get('details.svs.signing.damageLossDate')}</b></span>
              </div>
              <div className={styles.item}>
                {this.localization.getFormatedDate(this.props.docNotificationDamageLossDate, this.props.insuredPersonLanguageTC)}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <span>
            {this.props.policySubTypeTC === PolicySubTypeTCEnum.BELFIUS_HOME
              ? this.localization.get('details.svs.signing.belfiusInsurance')
              : this.localization.get('details.svs.signing.dvvInsurance')}
            {this.localization.formatString(
              'details.svs.signing.infoBeginning', {
                inspectorName: this.props.createdByName
                ?  this.props.createdByName + (this.props.createdByCompanyDibCode === Company.Belfius ? '' : ', ' + this.props.createdByCompany)
                : this.props.assignedUserName + (this.props.assignedUserParentDibCode === Company.Belfius ? '' : ', ' + this.props.assignedUserParentPartyName)
              })}&nbsp;
            {this.insuredData}&nbsp;
            {this.localization.get('details.svs.signing.infoEnd')}
          </span>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>
              <b>{this.localization.get('details.shared.insuredAmountBuilding')}</b>&nbsp;
              <BelfiusCurrencyText
                value={this.props.valueAndCause.cloned ? this.props.valueAndCause.cloneInsuredAmountBuilding : this.props.insuredAmountBuilding} suffix={' €'} />
            </span>
          </div>
          <div className={styles.item}>
            <span>
              <b>{this.localization.get('details.shared.insuredAmountContent')}</b>&nbsp;
              <BelfiusCurrencyText
                value={this.props.valueAndCause.cloned ? this.props.valueAndCause.cloneInsuredAmountContent : this.props.insuredAmountContent} suffix={' €'} />
            </span>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>
              <b>{this.localization.get('details.shared.valueBuildingBeforeAccident')}</b>&nbsp;
              {this.props.valueAndCause.valueBuildingBeforeAccident === 0 || this.props.valueAndCause.valueBuildingBeforeAccident === ''
                ? this.localization.get('details.shared.notChecked')
                : <BelfiusCurrencyText value={this.props.valueAndCause.valueBuildingBeforeAccident} prefix={'€ '} />}
            </span>
          </div>
          <div className={styles.item}>
            <span>
              <b>{this.localization.get('details.shared.valueContentBeforeAccident')}</b>&nbsp;
              {this.props.valueAndCause.valueContentBeforeAccident === 0 || this.props.valueAndCause.valueContentBeforeAccident === ''
                ? this.localization.get('details.shared.notChecked')
                : <BelfiusCurrencyText value={this.props.valueAndCause.valueContentBeforeAccident} prefix={'€ '} />}
            </span>
          </div>
        </div>
        <div className={styles.row}>
          <div className={`${styles.item} ${styles.border}`}>
            <div>
              <b>{this.localization.get('details.shared.damageCauseRemark')}</b>
            </div>
            <div className={styles.breakword}>{this.props.valueAndCause.damageCauseRemark}</div>
          </div>
        </div>
        <div className={`${styles.row} ${styles.borderBottom}`}>
          <div className={`${styles.item6x} ${styles.text}`}>
            <span><b>{this.localization.get('details.svs.svsLines.description')}</b></span>
          </div>
          <div className={`${styles.item} ${styles.text} ${styles.alignCenter}`}>
            <span><b>{this.localization.get('details.svs.svsLines.amount')}</b></span>
          </div>
        </div>
        {svsLinesOverview}
        <div className={styles.row}>
          <div className={`${styles.column} ${styles.redFont}`}>
            <div>
              <span>{getTypeCodeTranslation(this.props.SVSSubjectToTC, this.props.insuredPersonLanguageTC, this.props.excessApplicableTC)}</span>
            </div>
            <div>
              <span>{getTypeCodeTranslation(this.props.SVSApplicationOfTheExemptionTC, this.props.insuredPersonLanguageTC, this.props.underConditionTC)}</span>
            </div>
          </div>
        </div>
        <div className={`${styles.row} ${styles.borderTop}`}>
          <div className={styles.item6x}>
            <span><b>{this.localization.get('details.svs.svsLines.total')}</b></span>
          </div>
          <div className={`${styles.item} ${styles.alignRight}`}>
            <span><b><BelfiusCurrencyText value={this.props.svsLines.reduce((a, b) => +a + +b.amount, 0)} prefix={'€ '} /></b></span>
          </div>
        </div>
        <div className={styles.row}>
          <span>{this.localization.get('details.svs.signing.infoAmount')}</span>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>{this.localization.get('details.svs.signing.vatToBeJustified')}</span>
          </div>
          <div className={styles.item}>
            <span>
              6% {this.localization.get('details.svs.signing.amountOn')}&nbsp;
              <BelfiusCurrencyText value={this.props.vatAndPayment.vat6ToBeJustifiedAmount} suffix={' €'} />
            </span>
          </div>
          <div className={styles.item}>
            <span>
              21% {this.localization.get('details.svs.signing.amountOn')}&nbsp;
              <BelfiusCurrencyText value={this.props.vatAndPayment.vat21ToBeJustifiedAmount} suffix={' €'} />
            </span>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>{this.localization.get('details.svs.vatAndPayment.deductibleVatPercentage')}</span>
          </div>
          <div className={styles.item}>
            <span>{this.props.vatAndPayment.deductibleVatPercentage} %</span>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.item}>
            <span>{(this.props.vatAndPayment.iban || this.props.vatAndPayment.financialAccountNumber) && this.localization.get('details.svs.signing.paymentInfo')}</span>
          </div>
          <div className={styles.item}>
            <span>{(this.props.vatAndPayment.iban || this.props.vatAndPayment.financialAccountNumber)}</span>
          </div>
        </div>
        <div className={styles.border}>
          <div className={styles.row}>
            <div className={styles.item}>
              <span>
                {this.localization.get('details.svs.counterExpertise.nameTP')} {this.props.counterExpertise.nameTP}
              </span>
            </div>
            <div className={styles.item}>
              <span>
                {this.localization.get('details.svs.counterExpertise.nameTPCompany')} {this.props.counterExpertise.nameTPCompany}
              </span>
            </div>
            <div className={styles.item}>
              <span>
                {this.localization.get('details.svs.counterExpertise.referte')} {this.props.counterExpertise.referteTP}
              </span>
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.item}>
              <span>
                {this.localization.get('details.svs.counterExpertise.realValue')}&nbsp;
                <BelfiusCurrencyText value={this.props.counterExpertise.realValue} suffix='€' />
                <span><br /></span>
              </span>
              <span>{this.localization.get('details.svs.counterExpertise.info')}</span>
              <span><br /></span>
              <span><br /></span>
              <span className={styles.breakword}>{this.props.counterExpertise.comment}</span>
            </div>
            <div className={styles.item}>
              <span id={'counterExpertSignedName'}>
                {this.localization.get('details.svs.signing.counterExpertSignedName')}
              </span>
              <br />
              <br />
              <span>{this.props.counterExpertise.counterExpertSignedName}</span>
            </div>
            <div className={styles.item}>{this.signatureTemplate(this.props.signing.signatureCounterExpert, SignatureTypesEnum.COUNTER_EXPERT)}</div>
          </div>
        </div>
        <div className={styles.row}>
          <span>{this.localization.get('details.svs.signing.note')}</span>
        </div>
        <div className={styles.row}>
          <span>
            {this.localization.get('details.svs.signing.madeUp')} {this.props.insuredAddress}
            &nbsp;{this.localization.get('details.svs.signing.on')}&nbsp;
            {this.props.signing.signedOn ? this.props.signing.signedOn.toString() : this.localization.getFormatedDate(new Date(), this.props.insuredPersonLanguageTC)}
          </span>
        </div>
        <div className={styles.row}>
          <div className={`${styles.column} ${styles.margin1}`}>
            <div className={styles.item}>
              <span>{this.localization.get('details.svs.signing.forApproval')}</span>
            </div>
            <div className={styles.item}>
              <span>{this.localization.get('details.svs.signing.personalExpert')}</span>
            </div>
            <div className={styles.item}>
              {this.personalExpertSignedName}
            </div>
            {this.signatureTemplate(this.props.signing.signaturePersonalExpert, SignatureTypesEnum.PERSONAL_EXPERT)}
          </div>
          <div className={`${styles.column} ${styles.margin1}`}>
            <div className={styles.item}>
              <span>{this.localization.get('details.svs.signing.forApproval')}</span>
            </div>
            <div className={styles.item}>
              <span>{this.localization.get('details.svs.signing.insuredPerson')}</span>
            </div>
            <div className={styles.item}>
              <span>{this.props.insuredPersonName}</span>
            </div>
            <div className={styles.item}>
              <span className={styles.reflectionTimeInfoStyle}>
                <Checkbox
                  disabled={this.props.locked}
                  checked={Boolean(this.props.signing.reflectionTime)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    this.props.updateValues({ ...this.props.signing, reflectionTime: event.target.checked });
                  }}
                  value='true' />
                {this.localization.get('details.svs.signing.reflectionTime')}
              </span>
            </div>
            {this.signatureTemplate(this.props.signing.signaturePolicyHolder, SignatureTypesEnum.POLICY_HOLDER)}
          </div>
          <div className={`${styles.column} ${styles.margin1}`}>
            <div className={styles.item}>
              <span>{this.localization.get('details.svs.signing.forApproval')}</span>
            </div>
            <div className={styles.item}>
              <span>{this.props.policySubTypeTC === PolicySubTypeTCEnum.BELFIUS_HOME
                ? this.localization.get('details.svs.signing.belfiusInsurance')
                : this.localization.get('details.svs.signing.dvvInsurance')}</span>
            </div>
            <div className={styles.item}>
              <span>
                {this.props.createdByName ?
                this.props.createdByName + (this.props.createdByCompanyDibCode === Company.Belfius ? '' : ', ' + this.props.createdByCompany)
                : this.props.assignedUserName + (this.props.assignedUserParentDibCode === Company.Belfius ? '' : ', ' + this.props.assignedUserParentPartyName)}</span>
            </div>
            {this.signatureTemplate(this.props.signing.signatureExpertOrInspector, SignatureTypesEnum.EXPERT_OR_INSPECTOR)}
          </div>
        </div>
        <div className={styles.rowNoPadding}>
          <span className={styles.reflectionTimeInfoStyle}>{this.localization.get('details.svs.signing.reflectionTimeInformation')}</span>
        </div>
        <div className={styles.row}>
          <span>
            <b>{this.localization.get('details.svs.remark.placeholder')}</b>
          </span>
        </div>
        <div className={styles.row}>
          <div className={`${styles.item} ${styles.border}`}>
            <div>{this.props.remark}</div>
          </div>
        </div>
        <div className={`${styles.row} ${styles.alignCenter}`}>
          {this.endText}
        </div>
        <Modal 
          aria-labelledby='simple-modal-title' 
          aria-describedby='simple-modal-description'
          open={this.state.modalOpened}
          onClose={() => this.closeSignatureModal()}
          component="div">
          <div className={styles.modal}>
            <SignatureCanvas
                canvasProps={{width: this.state.signaturePadWidth, height: this.state.signaturePadHeight, className: styles.signaturePadStyle }}
                ref={this.signatureCanvas}
              />
            <div>
              <Fab
                aria-label='Done'
                className={styles.button}
                onClick={() => this.addSignature()}
              ><DoneIcon /></Fab>
              <Fab
                aria-label='Close'
                className={styles.button}
                onClick={() => this.closeSignatureModal()}
              ><ClearIcon /></Fab>
              <Fab
                aria-label='Clear'
                className={styles.button}
                onClick={() => this.handleClear()}>
                <img alt="" className={styles.actionIcon}
                  src={require('../../../../assets/images/erase.png')} />
              </Fab>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = ({ details: { detailsSvs, detailsInsuredPerson, detailsPolicy }, caseState: { selectedCase }, shared: { roles } }: ApplicationState): PropsFromState => ({
  insuredAmountBuilding: detailsPolicy.policy.insuredAmountBuilding,
  insuredAmountContent: detailsPolicy.policy.insuredAmountContent,
  valueAndCause: detailsSvs.valueAndCause,
  svsLines: detailsSvs.lines,
  vatAndPayment: detailsSvs.vatAndPayment,
  counterExpertise: detailsSvs.counterExpertise,
  insuredPersonName: detailsSvs.additionalData.insuredPersonName,
  insuredAddress: detailsSvs.additionalData.insuredAddress,
  createdByName: detailsSvs.createdByName,
  createdByCompany: detailsSvs.createdByCompany,
  createdByCompanyDibCode: detailsSvs.createdByCompanyDibCode,
  signing: detailsSvs.signing,
  caseNumber: detailsPolicy.policy.damageCaseCode,
  policyCode: detailsPolicy.policy.policyCode,
  docNotificationDamageLossDate: detailsPolicy.policy.docNotificationDamageLossDate,
  remark: detailsSvs.remark.remark,
  locked: detailsSvs.locked || selectedCase.blocked || !roles.includes(Role.ExFiLoAd),
  validated: detailsSvs.validated,
  insuredPersonLanguageTC: detailsInsuredPerson.languageTC,
  policySubTypeTC: detailsSvs.additionalData.policySubTypeTC,
  contractName: detailsSvs.additionalData.contractName,
  SVSSubjectToTC: detailsSvs.SVSSubjectToTC,
  SVSApplicationOfTheExemptionTC: detailsSvs.SVSApplicationOfTheExemptionTC,
  excessApplicableTC: detailsSvs.excessApplicableTC,
  underConditionTC: detailsSvs.underConditionTC,
  svsBlocked: selectedCase.blocked || !roles.includes(Role.ExFiLoAd),
  caseCode: selectedCase.caseCode,
  assignedUserName: selectedCase.expertName,
  assignedUserParentPartyName: selectedCase.parentPartyName,
  assignedUserParentDibCode: selectedCase.parentPartyDibCode,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  updateSigning: (signing: SigningType, caseCode: string) => dispatch(updateSigning(signing, caseCode)),
  updateValues: (signing: SigningType) => dispatch(tabSigningFieldChange(signing)),
  validateSvs: () => dispatch(validateSvs()),
  setValidated: (value: boolean) => dispatch(setValidated(value))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Signing);
